import React, { useRef, useState } from 'react';
import AuctionCard from '../auction-card/index';
import { Stack, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from '../../../constants/route-constants';
import InfiniteScroll from 'react-infinite-scroller';
import NoData from '../no-data/index';
import PropTypes from 'prop-types';
import SkeletonLoader from '../../common/skeleton-loader';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect } from 'react';
import { APP_KEYS, NUMBER, PAGE_SIZE } from '../../../constants/app-constants';
import { prepareGADataAndSendEvent } from '../../../tracking';
import { isValidDealer } from '../../../utils/is-valid-dealer';
import { InView } from 'react-intersection-observer';

let interval;
const carouselSettings = {
    slidesPerView: 1,
};

const Auctions = ({ auctionType, user, auctionsList, getAuctionsListConnect, setSearchFlagConnect, fetchNotificationsConnect }) => {
    const { results: auctions, total, isSearchResults, isLoading } = auctionsList || {};
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const ref = useRef({ currentPage: 0, pageSize: PAGE_SIZE, search: '', searchedTotalCount: 0, callNonSearch: false });
    const navigate = useNavigate();
    const eligibleForBid = isValidDealer(user?.status);
    const getAllAuctions = (payload, ...rest) => {
        if (eligibleForBid) {
            return new Promise((resolve, reject) => {
                getAuctionsListConnect({ ...payload, section: auctionType?.join(',') }, ...rest).then((res) => {
                    resolve(res);
                }).catch(error => reject(error));
            });
        }
    };

    useEffect(() => {
        if (eligibleForBid) {
            if (isSearchResults) {
                ref.current.searchedTotalCount = total;
                ref.current.search = isSearchResults;
                // If searched count less than page size, we fetch other list(non-matched) also. If this case fails then loadMoreData function handles pagination itself
                if (ref.current.searchedTotalCount < ref.current.pageSize) {
                    ref.current.currentPage = 0;
                    const payload = { page: ref.current.currentPage, size: ref.current.pageSize, type: APP_KEYS.NON_SEARCH, search: isSearchResults };
                    getAllAuctions(payload, true);
                }
            }
            // For auto-refresh auctions list for at 1minute interval
            interval = setInterval(() => {
                ref.current.currentPage = 0;
                handleSearchAuctions(false);
            }, NUMBER.SIXTY_THOUSAND);
        }
        // Clear the interval if component unmounts
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearchResults, auctionType]);

    const handleSearchAuctions = (loader) => {
        // Handling search case -> searched auctions comes first if length is less then non-searched auctions
        if (isSearchResults) {
            const searchPayload = { page: 0, size: ref.current.pageSize, type: APP_KEYS.SEARCH, search: isSearchResults };
            getAllAuctions(searchPayload, false, loader).then(res => {
                if (res?.total < PAGE_SIZE) {
                    const nonSearchPayload = { page: 0, size: ref.current.pageSize, type: APP_KEYS.NON_SEARCH, search: isSearchResults };
                    getAllAuctions(nonSearchPayload, true, loader);
                }
            });
        } else {
            // Handling usual case
            fetchFreshAuctions(loader);
        }
    };

    useEffect(() => {
        if (eligibleForBid) {
            handleSearchAuctions(true);
            fetchNotificationsConnect({ page: 1, size: ref.current.pageSize });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auctionType]);

    const loadMoreData = async () => {
        try {
            let searchNonSearchTotal;
            const isSearchHasPagination = ref.current.searchedTotalCount > ref.current.pageSize && auctions.length > ref.current.searchedTotalCount;
            if ((ref.current.searchedTotalCount &&
                ref.current.searchedTotalCount < ref.current.pageSize &&
                (ref.current.searchedTotalCount !== total || (ref.current.searchedTotalCount === total && auctions?.length !== total)))
                || isSearchHasPagination
            ) {
                // If searched total exists and it is different from current total count OR search & non search count same but current auction list length not same
                searchNonSearchTotal = ref.current.searchedTotalCount + total;
            } else searchNonSearchTotal = total;
            // searched auctions pagination or searched & non searched combined auctions count;
            const currentAuctionsCount = ref.current.searchedTotalCount < ref.current.pageSize ? (auctions?.length - ref.current.searchedTotalCount) : isSearchHasPagination ? (auctions?.length - ref.current.searchedTotalCount) : auctions?.length;
            // load next page if page size and current auctions count same
            if (auctions?.length < searchNonSearchTotal && (ref.current.currentPage + 1) === (currentAuctionsCount / ref.current.pageSize)) {
                ref.current.currentPage += 1;
                let payload = { page: ref.current.currentPage, size: ref.current.pageSize };
                if (isSearchResults) {
                    // Add search related payload data if search has happened
                    payload = { ...payload, search: ref.current.search, type: auctions?.length < ref.current.searchedTotalCount ? APP_KEYS.SEARCH : APP_KEYS.NON_SEARCH };
                }
                fetchAuctions(payload);
                // Search pagination ends then start non search pagination
            } else if (ref.current.searchedTotalCount > ref.current.pageSize && searchNonSearchTotal === auctions?.length && !ref.current?.callNonSearch) {
                ref.current.currentPage = 0;
                ref.current.callNonSearch = true;
                const payload = { page: ref.current.currentPage, size: ref.current.pageSize, search: ref.current.search, type: APP_KEYS.NON_SEARCH };
                fetchAuctions(payload);
            }
        } catch (error) {
        }
    };

    const fetchAuctions = (payload) => {
        setIsLoadingMore(true);
        getAllAuctions(payload, true, false).finally(() => setIsLoadingMore(false));
    };

    const fetchFreshAuctions = (isLoader) => {
        const payload = { page: 0, size: ref.current.pageSize };
        getAllAuctions(payload, false, isLoader);
    };

    const handleRefresh = () => {
        prepareGADataAndSendEvent('refreshClick', user, null, 'Explore page');
        if (isSearchResults) setSearchFlagConnect(false);
        ref.current.currentPage = 0;
        ref.current.search = '';
        ref.current.searchedTotalCount = 0;
        fetchFreshAuctions(true);
    };

    const handleInViewChange = (auctionData, idx, isInView) => {
        if (isInView) {
            prepareGADataAndSendEvent('viewItemList', user, auctionData, `Auction list`, { index: idx, eventLabel: auctions?.length });
        }
    };

    const handleImageClick = (auction, data) => {
        const scrollPercentage = Math.round((data?.index / data?.total) * 100) || 0;
        prepareGADataAndSendEvent('imageHorizontalScrollDepth', user, auction, 'Explore page', { eventLabel: !data?.key ? data?.category : data?.url, index: data?.index, genericLabel: `${scrollPercentage}%` });
    };

    const handleCardClick = (auctionId) => {
        navigate(`${ROUTE_PATH.EXPLORE}${ROUTE_PATH.AUCTION_DETAILS}?aid=${auctionId}`);
    };
    const isData = (auctions && auctions.length > 0);
    return (
        <React.Fragment>
            <Stack direction={'row'} spacing={1} alignItems="center" justifyContent='flex-end' className='mb-4 absolute top-0 right-0'>
                <Button size='small' onClick={handleRefresh} className='bg-tab-bg w-10 min-w-max p-1'><RefreshIcon />
                </Button>
            </Stack>
            {isData && !isLoading &&
                <React.Fragment>
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMoreData}
                        threshold={50}
                        hasMore={!!auctions?.length <= total}
                    >
                        <Stack spacing={3} direction={'column'}>
                            {auctions?.map((auction, index) => (
                                <InView key={index} as="div" onChange={handleInViewChange.bind(this, auction, index)}>
                                    <div className='min-height-250'>
                                        <AuctionCard carouselSettings={carouselSettings} fetchFreshAuctions={handleRefresh} isSlider={true} auction={auction} handleCardClick={handleCardClick.bind(null, auction?.appointmentId)} handleImageClick={handleImageClick.bind(this, auction)} />
                                    </div>
                                </InView>
                            ))}
                        </Stack>
                    </InfiniteScroll>
                </React.Fragment>
            }
            {(isLoading || isLoadingMore) && <SkeletonLoader />}
            {!isData && !isLoading && !isLoadingMore && <NoData title={'Oops! No auctions running.'} subTitle='Come back soon!' handleRefresh={handleRefresh} />}
        </React.Fragment>
    );
};

Auctions.propTypes = {
    user: PropTypes.object,
    auctionType: PropTypes.string,
    auctionsList: PropTypes.object,
    getAuctionsListConnect: PropTypes.func,
    setSearchFlagConnect: PropTypes.func,
    fetchNotificationsConnect: PropTypes.func
};

export default Auctions;
